import { PrimaryDefaultButton } from '@/components/buttons/primary';
import SecondaryDefaultButton from '@/components/buttons/secondary';
import SimpleHero from '@/components/heros/SimpleHero';
import Image from '@/components/Image';
import Link from '@/components/Link';
import { ModuleSimpleHeroFragment } from '@/graphql';
import { getSingleLink } from '@/lib/craft-utils';
import gtm from '@/lib/gtm';
import { parseHtml } from '@/lib/parse-html';
import { getSingle, maybeNotSet } from '@/lib/typescript-helpers';
import { FC, ReactNode } from 'react';

export type CTA = ({ isInverted }: { isInverted: boolean }) => ReactNode;

export interface SimpleHeroModuleProps extends PropsWithClassName {
  props: CraftModuleFragment<ModuleSimpleHeroFragment>;
  assetOverride?: ReactNode;
  ctaOverride?: ({ isInverted, ctas }: { isInverted: boolean; ctas: ReactNode }) => ReactNode;
}

const SimpleHeroModule: FC<SimpleHeroModuleProps> = ({ props, assetOverride, ctaOverride, className }) => {
  if (Array.isArray(props)) {
    props = props[0];
  }

  if (!props) return null;

  let primaryCta: ReactNode = null;
  let allowSecondaryCta = true;

  const primaryCtaLink = getSingleLink(props.primaryCtaLink);
  const secondaryCtaLink = getSingleLink(props.secondaryCtaLink);

  switch (props.primaryCtaType) {
    case 'standaloneCta':
      if (!primaryCtaLink) break;

      primaryCta = (
        <Link href={primaryCtaLink.url}>
          <PrimaryDefaultButton
            as="a"
            onClick={() => {
              gtm.track('click', {
                element_type: 'button',
                location: 'Hero',
                element_label: maybeNotSet(primaryCtaLink.text),
              });
            }}
          >
            {primaryCtaLink.text}
          </PrimaryDefaultButton>
        </Link>
      );

      break;

    case 'emailCaptureAndCta':
      if (!primaryCtaLink) break;

      allowSecondaryCta = false;
      primaryCta = <SimpleHero.GroupedInput buttonText={primaryCtaLink.text ?? ''} />;
      break;

    case 'none':
    default:
      primaryCta = null;
  }

  const secondaryCta: ReactNode =
    allowSecondaryCta && secondaryCtaLink ? (
      <Link href={secondaryCtaLink.url}>
        <SecondaryDefaultButton
          as="a"
          onClick={() => {
            gtm.track('click', {
              element_type: 'button',
              location: 'Hero',
              element_label: maybeNotSet(secondaryCtaLink.text),
            });
          }}
        >
          {secondaryCtaLink.text}
        </SecondaryDefaultButton>
      </Link>
    ) : null;

  const asset = getSingle(props.heroAsset);

  let assetComponent: ReactNode = null;

  if (asset && asset.url && asset.url) {
    if (asset.url.endsWith('.mp4')) {
      // Asset is a video file
      if (props.videoAssetBehavior === 'video') {
        assetComponent = (
          <SimpleHero.VideoWrapper src={asset.url} width={asset.width ?? 0} height={asset.height ?? 0} />
        );
      } else {
        assetComponent = (
          <video key={asset.url} width={asset.width ?? 0} height={asset.height ?? 0} loop autoPlay muted playsInline>
            <source src={asset.url} />
          </video>
        );
      }
    } else {
      // Asset is an image file
      assetComponent = (
        <Image key={asset.url} src={asset.url} width={asset.width ?? 0} height={asset.height ?? 0} alt="" />
      );
    }
  }

  return (
    <SimpleHero
      className={className}
      eyebrow={props.eyebrow ?? undefined}
      heading={props.heading ?? ''}
      background={props.background ?? undefined}
      asset={assetOverride ?? assetComponent}
      ctas={({ isInverted }) => {
        const ctas = (
          <>
            {primaryCta}
            {secondaryCta}
          </>
        );

        if (ctaOverride) {
          return ctaOverride({ ctas, isInverted });
        }

        if (!primaryCta && !secondaryCta) return null;

        return ctas;
      }}
    >
      {parseHtml(props.text)}
    </SimpleHero>
  );
};

export default SimpleHeroModule;
